//@flow strict
import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {
  Card,
  CardFooter,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {Checkbox} from '@spaced-out/ui-design-system/lib/components/Checkbox';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';

import {SimpleDropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import {useSelector, useDispatch} from 'react-redux';
import {selectApplicantInformation} from 'src/selectors/crm';
import {updateCrmApplicantInformationData} from 'src/action-creators/crm.js';

// $FlowFixMe[nonstrict-import]
import * as reduxApi from 'src/utils/redux-api-v2';
import css from './edit-experience-card.css';


type ActionType = 'ADD' | 'EDIT' | 'DELETE';

export type ExperienceType = {|
  experienceOrg: string,
  title: string,
  startMonth: number | string,
  startYear: string,
  endMonth: number | string,
  endYear: string,
  location: string,
  isCurrent: boolean,
|};

type ExperienceCardProps = {|
  setAddExperienceCardOpen: (open: boolean) => void,
  experiences: Array<ExperienceType>,
  setExperiences: (experiences: Array<ExperienceType>) => void,
  editIndex?: number,
|};

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const years = Array.from(
  {length: new Date().getFullYear() - 1969 + 1},
  (_, i) => 1969 + i,
);

export const ExperienceCard = ({
  setAddExperienceCardOpen,
  experiences = [],
  setExperiences,
  editIndex,
}: ExperienceCardProps): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [isLoading, setIsLoading] = React.useState(false);
  const dispatch = useDispatch();
  const applicantDetails = useSelector(selectApplicantInformation);

  // Get experience data if editing
  const experience = editIndex !== undefined ? experiences[editIndex] : null;
  const {
    experienceOrg = '',
    title = '',
    location = '',
    startMonth = '',
    startYear = '',
    endMonth = '',
    endYear = '',
    isCurrent = false,
  } = experience || {};

  const [formData, setFormData] = React.useState({
    experienceOrg,
    title,
    location,
    startMonth: startMonth ? startMonth.toString() : '',
    startYear: startYear ? startYear.toString() : '',
    endMonth: endMonth ? endMonth.toString() : '',
    endYear: endYear ? endYear.toString() : '',
    isCurrent,
  });

  const [isCurrentlyWorking, setIsCurrentlyWorking] = React.useState(
    formData.isCurrent,
  );

  const handleInputChange = (field: string, value: string | number | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isFormValid = () => {
    const {
      experienceOrg,
      title,
      location,
      startMonth,
      startYear,
      endMonth,
      endYear,
    } = formData;
    const fieldsValid =
      experienceOrg.trim() &&
      title.trim() &&
      location.trim() &&
      startMonth !== null &&
      startYear !== null &&
      (isCurrentlyWorking || (endMonth !== null && endYear !== null));

    let datesValid = true;
    if (!isCurrentlyWorking && endMonth !== null && endYear !== null) {
      // Compare years first
      if (startYear > endYear) {
        datesValid = false;
      }
      // If same year, compare months
      else if (startYear === endYear && startMonth > endMonth) {
        datesValid = false;
      }
    }

    return fieldsValid && datesValid;
  };

  const handleFormSubmit = async (type: ActionType) => {
    if (!isFormValid() && type !== 'DELETE') {
      return;
    }

    const newExperience: ExperienceType = {
      experienceOrg: formData.experienceOrg,
      title: formData.title,
      location: formData.location,
      startMonth: formData.startMonth,
      startYear: formData.startYear,
      endMonth: isCurrentlyWorking ? '' : formData.endMonth,
      endYear: isCurrentlyWorking ? '' : formData.endYear,
      isCurrent: isCurrentlyWorking,
    };

    const submitData = {
      section: 'EXPERIENCE',
      editFields: {
        data: type === 'DELETE' ? {} : newExperience,
        type,
        index: editIndex,
      },
    };

    try {
      setIsLoading(true);
      const response = await dispatch(
        reduxApi.post(
          `api/candidates/${applicantDetails.id}`,
          submitData,
          {},
          {apiPath: '/trmapi/'},
        ),
      );

      const updatedExperiences = [...experiences];
      switch (type) {
        case 'ADD':
          updatedExperiences.push(newExperience);
          break;
        case 'EDIT':
          if (
            editIndex !== undefined &&
            editIndex >= 0 &&
            editIndex < updatedExperiences.length
          ) {
            updatedExperiences[editIndex] = newExperience;
          } else {
            console.error('Invalid editIndex:', editIndex);
            return;
          }
          break;
        case 'DELETE':
          if (
            editIndex !== undefined &&
            editIndex >= 0 &&
            editIndex < updatedExperiences.length
          ) {
            updatedExperiences.splice(editIndex, 1);
          } else {
            console.error('Invalid editIndex:', editIndex);
            return;
          }
          break;
      }
      setExperiences(updatedExperiences);
      setAddExperienceCardOpen(false);
      dispatch(
        updateCrmApplicantInformationData({
          ...applicantDetails,
          ...response,
        }),
      );
      //   dispatch(getApplicantInformation(applicantDetails.id))
    } catch (error) {
      console.error('Error submitting experience:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const inputFields = [
    {
      label: t('Company', 'Company'),
      placeholder: t('Enter_company', 'Enter company'),
      field: 'experienceOrg',
    },
    {
      label: t('Job_Title', 'Job Title'),
      placeholder: t('Enter_job_title', 'Enter job title'),
      field: 'title',
    },
    {
      label: t('Location', 'Location'),
      placeholder: t('Enter_location', 'Enter location'),
      field: 'location',
    },
  ];

  return (
    <Card classNames={{wrapper: css.cardWrapper}}>
      {inputFields.map(({label, placeholder, field}) => (
        <Input
          key={field}
          label={label}
          placeholder={placeholder}
          required
          value={formData[field]}
          onChange={(e) => handleInputChange(field, e.target.value)}
          disabled={isLoading}
        />
      ))}

      <div className={css.twoInputContainer}>
        <SimpleDropdown
          label={t('Start_Date', 'Start Date')}
          placeholder={t('month', 'month')}
          options={months.map((month, index) => ({
            key: index.toString(),
            label: month,
          }))}
          selectedKeys={
            formData.startMonth !== null ? [formData.startMonth] : []
          }
          onChange={(option) => handleInputChange('startMonth', option.key)}
          disabled={isLoading}
        />

        <SimpleDropdown
          placeholder={t('years', 'years')}
          options={years.map((year) => ({
            key: year.toString(),
            label: year.toString(),
          }))}
          classNames={{wrapper: css.maxExpInput}}
          selectedKeys={formData.startYear !== null ? [formData.startYear] : []}
          onChange={(option) => handleInputChange('startYear', option.key)}
          disabled={isLoading}
        />
      </div>

      <Checkbox
        checked={isCurrentlyWorking}
        onChange={() => setIsCurrentlyWorking(!isCurrentlyWorking)}
        children={t(
          'I_am_currently_working_here',
          'I am currently working here',
        )}
        disabled={isLoading}
      />

      {!isCurrentlyWorking && (
        <div className={css.twoInputContainer}>
          <SimpleDropdown
            label={t('End_Date', 'End Date')}
            placeholder={t('month', 'month')}
            options={months.map((month, index) => ({
              key: index.toString(),
              label: month,
            }))}
            selectedKeys={formData.endMonth !== null ? [formData.endMonth] : []}
            onChange={(option) => handleInputChange('endMonth', option.key)}
            disabled={isLoading}
          />
          <SimpleDropdown
            placeholder={t('years', 'years')}
            options={years.map((year) => ({
              key: year.toString(),
              label: year.toString(),
            }))}
            classNames={{wrapper: css.maxExpInput}}
            selectedKeys={formData.endYear !== null ? [formData.endYear] : []}
            onChange={(option) => handleInputChange('endYear', option.key)}
            disabled={isLoading}
          />
        </div>
      )}

      <CardFooter>
        <div className={css.buttonContainer}>
          <div className={css.buttonWrapper}>
            {editIndex !== undefined && (
              <Button
                iconRightName="trash"
                classNames={{
                  wrapper: css.trashButton,
                  icon: css.trashIcon,
                }}
                onClick={() => handleFormSubmit('DELETE')}
                disabled={isLoading}
              />
            )}
          </div>
          <div>
            <Button
              type="ghost"
              children={t('Cancel', 'Cancel')}
              onClick={() => setAddExperienceCardOpen(false)}
              disabled={isLoading}
            />
            <Button
              type="primary"
              children={t('Apply', 'Apply')}
              onClick={() =>
                handleFormSubmit(editIndex !== undefined ? 'EDIT' : 'ADD')
              }
              disabled={isLoading || !isFormValid()}
            />
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};
